import { IconUploadFile } from '@doltech/icons-from-dev/IconUploadFile';
import { DEFAULT_FILE_UPLOAD_LIMIT_IN_MB } from '@doltech/utils/lib/constants';
import { imgProxyLoader } from '@doltech/utils/lib/image-loader';
import { isBytesExceedMb } from '@doltech/utils/lib/object';
import { Spin } from 'antd';
import cl from 'classnames';
import { eq, noop } from 'lodash';
import Image from 'next/image';
import * as React from 'react';
import styled from 'styled-components';

import { DefaultPublicImageUpload, DefaultPublicImageUploadWithoutDrag, FileUploadProps } from '.';
import no_image from '../../images/No_image_available.png';
import { Button } from '../ButtonV2/ButtonV2';
import { colorsV2 } from '../colors-v2';
import { InputAltComponent } from '../DolEditor/elements/BlogEditor/MediaLogic/component/InputAltComponent';
import { showToast, showToastBase } from '../Toast';

const Thumbnail = styled.div<{ width?: number }>`
  img {
    background-color: ${colorsV2.gray20};
    border-radius: 0;

    &.rounded {
      border-radius: 8px;
    }
  }
`;

export interface ThumbnailFileUploadProps extends FileUploadProps {
  width?: number;
  ratio?: number; // Ex: 3/2 , 1, 16 / 9
  rounded?: boolean;
  onImageChange?: (image: any) => void;
}

export const ThumbnailFileUpload = (props: ThumbnailFileUploadProps) => {
  const {
    required,
    sizeLimitInMb = DEFAULT_FILE_UPLOAD_LIMIT_IN_MB,
    width,
    ratio,
    onProgress,
    onImageChange = noop,
    fileInfo,
    rounded,
    ...rest
  } = props;

  const [uploading, setUploading] = React.useState(false);

  const onImageUploadedSuccess = (res) => {
    setUploading(false);
    const {
      tokenResponse,
      file: { name, size, uploadedDate },
    } = res;
    const { path, downloadResourceUrl } = tokenResponse;
    const uploadedImage = {
      url: downloadResourceUrl,
      path,
      name,
      size,
      uploadedDate,
      alt: '',
    };
    onImageChange(uploadedImage);
  };

  const onImageUploadedError = (err) => {
    setUploading(false);
    showToast({
      icon: 'danger',
      title: 'File upload failed.',
      subTitle: err.message,
    });
  };

  const onUploadProgress = (...args) => {
    setUploading(true);
    onProgress?.(...args);
  };

  const beforeUpload = (fileUpload) => {
    const isExceeedLimit = isBytesExceedMb(fileUpload.size, sizeLimitInMb);
    if (isExceeedLimit) {
      showToastBase({
        icon: 'error',
        title: `Can't upload file: ${fileUpload.name}`,
        subTitle: `Please Upload File Smaller Than ${sizeLimitInMb * 1000}kb`,
      });
      return false;
    }

    return true;
  };

  if (fileInfo) {
    return (
      <Thumbnail style={{ width: width || '100%' }}>
        <Image
          className={cl({ rounded })}
          width={1}
          height={1 / ratio}
          alt={fileInfo.alt || ''}
          src={fileInfo.url || no_image}
          quality={30}
          objectFit="cover"
          layout="responsive"
          loader={imgProxyLoader}
        />
        <InputAltComponent
          alt={fileInfo?.alt || ''}
          onUpdateAlt={(alt) => {
            if (eq(alt, fileInfo?.alt)) {
              onImageChange({
                ...fileInfo,
                alt,
              });
            }
          }}
        />
        <DefaultPublicImageUploadWithoutDrag
          disabled
          onProgress={onUploadProgress}
          beforeUpload={beforeUpload}
          onSuccess={(res) => onImageUploadedSuccess(res)}
          onError={(err) => onImageUploadedError(err)}
          {...rest}
        >
          <Button.Primary variant="white">Replace</Button.Primary>
        </DefaultPublicImageUploadWithoutDrag>
      </Thumbnail>
    );
  }

  return (
    <Spin spinning={uploading}>
      <DefaultPublicImageUpload
        style={required ? { borderColor: colorsV2.red100 } : {}}
        onProgress={onUploadProgress}
        beforeUpload={beforeUpload}
        onSuccess={(res) => onImageUploadedSuccess(res)}
        onError={(err) => onImageUploadedError(err)}
        {...rest}
      >
        <IconUploadFile />
      </DefaultPublicImageUpload>
    </Spin>
  );
};
